import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Spin,
  Descriptions,
  Timeline,
  Input,
  Select,
  Breadcrumb,
  Divider,
  Avatar,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SendOutlined,
  UserOutlined,
  DesktopOutlined,
  LinkOutlined,
  EditOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function TicketDetail() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [newStatus, setNewStatus] = useState(null);

  useEffect(() => {
    // No backend API for tickets yet - will fetch from Zendesk API when integration is implemented
    setLoading(false);
  }, [ticketId]);

  const handleAddComment = () => {
    if (!newComment.trim()) return;

    const comment = {
      id: Date.now(),
      type: "comment",
      user: "Partner Support",
      userType: "partner",
      content: newComment,
      timestamp: dayjs().format("YYYY-MM-DD HH:mm"),
    };

    setTicket({
      ...ticket,
      history: [...ticket.history, comment],
      updatedAt: dayjs().format("YYYY-MM-DD HH:mm"),
    });
    setNewComment("");
  };

  const handleStatusChange = (status) => {
    setNewStatus(status);
    const statusChange = {
      id: Date.now(),
      type: "status_change",
      user: "Partner Support",
      content: `Status changed from "${ticket.status}" to "${status}"`,
      timestamp: dayjs().format("YYYY-MM-DD HH:mm"),
    };

    setTicket({
      ...ticket,
      status: status,
      history: [...ticket.history, statusChange],
      updatedAt: dayjs().format("YYYY-MM-DD HH:mm"),
    });
  };

  const getPriorityTag = (priority) => {
    const colors = { high: "red", medium: "orange", low: "blue" };
    return <Tag color={colors[priority]}>{priority.toUpperCase()}</Tag>;
  };

  const getStatusTag = (status) => {
    const config = {
      open: { color: "error", icon: <ExclamationCircleOutlined /> },
      ongoing: { color: "processing", icon: <ClockCircleOutlined /> },
      closed: { color: "success", icon: <CheckCircleOutlined /> },
    };
    return (
      <Tag color={config[status].color} icon={config[status].icon} style={{ fontSize: 14, padding: "4px 12px" }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Tag>
    );
  };

  const getTimelineItemStyle = (type) => {
    switch (type) {
      case "created":
        return { color: token.colorPrimary };
      case "comment":
        return { color: token.colorSuccess };
      case "status_change":
        return { color: token.colorWarning };
      case "priority_change":
        return { color: token.colorError };
      default:
        return { color: token.colorTextSecondary };
    }
  };

  const getUserAvatar = (userType) => {
    const styles = {
      system: { background: "linear-gradient(135deg, #f5a623 0%, #d48806 100%)" },
      partner: { background: "linear-gradient(135deg, #05a677 0%, #048a63 100%)" },
      powow: { background: "linear-gradient(135deg, #54A2CC 0%, #3d8ab8 100%)" },
    };
    return styles[userType] || { background: token.colorPrimary };
  };

  if (loading) {
    return (
      <PageLayout active="ticketsSummary">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!ticket) {
    return (
      <PageLayout active="ticketsSummary">
        <div style={{ textAlign: "center", padding: 48 }}>
          <Text type="secondary">Ticket not found</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate("/tickets/summary")}>
              Back to Tickets
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="ticketsSummary">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate("/tickets/summary")}>Tickets</a> },
          { title: ticket.id },
        ]}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/tickets/summary")}>
            Back
          </Button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <Title level={3} style={{ marginBottom: 0 }}>{ticket.id}</Title>
              {getStatusTag(ticket.status)}
              {getPriorityTag(ticket.priority)}
            </div>
            <Text style={{ fontSize: 16 }}>{ticket.title}</Text>
          </div>
        </div>

        <Space>
          <Select
            value={ticket.status}
            onChange={handleStatusChange}
            style={{ width: 140 }}
            options={[
              { value: "open", label: "Open" },
              { value: "ongoing", label: "Ongoing" },
              { value: "closed", label: "Closed" },
            ]}
          />
          <Button icon={<EditOutlined />}>Edit</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {/* Left Column - Ticket Details */}
        <Col xs={24} lg={16}>
          {/* Description */}
          <Card
            bordered={false}
            style={{ borderRadius: 12, marginBottom: 16 }}
            title={<Text strong>Description</Text>}
          >
            <Paragraph style={{ fontSize: 14, marginBottom: 0 }}>
              {ticket.description}
            </Paragraph>
            <Divider />
            <Space wrap>
              {ticket.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Space>
          </Card>

          {/* Timeline / History */}
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={<Text strong>Activity History</Text>}
          >
            {/* Add Comment */}
            <div style={{ marginBottom: 24, display: "flex", gap: 12 }}>
              <TextArea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleAddComment}
                style={{ alignSelf: "flex-end" }}
              >
                Add
              </Button>
            </div>

            <Timeline
              items={[...ticket.history].reverse().map((item) => ({
                dot: (
                  <Avatar size="small" style={getUserAvatar(item.userType)} icon={<UserOutlined />} />
                ),
                children: (
                  <div style={{ paddingBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <Space size={8}>
                        <Text strong style={{ fontSize: 13 }}>{item.user}</Text>
                        <Tag
                          color={
                            item.type === "created"
                              ? "blue"
                              : item.type === "comment"
                              ? "green"
                              : item.type === "status_change"
                              ? "orange"
                              : "red"
                          }
                          style={{ fontSize: 10 }}
                        >
                          {item.type.replace("_", " ")}
                        </Tag>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 11 }}>{item.timestamp}</Text>
                    </div>
                    <Paragraph style={{ margin: 0, fontSize: 13, color: token.colorTextSecondary }}>
                      {item.content}
                    </Paragraph>
                  </div>
                ),
              }))}
            />
          </Card>
        </Col>

        {/* Right Column - Related Info */}
        <Col xs={24} lg={8}>
          {/* System Info */}
          <Card
            bordered={false}
            style={{ borderRadius: 12, marginBottom: 16 }}
            title={
              <Space>
                <DesktopOutlined />
                <Text strong>Related System</Text>
              </Space>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="System SN">
                <Button
                  type="link"
                  onClick={() => navigate(`/systems/${ticket.systemSn}`)}
                  style={{ padding: 0, height: "auto" }}
                  icon={<LinkOutlined />}
                >
                  {ticket.systemSn}
                </Button>
              </Descriptions.Item>
              <Descriptions.Item label="NMI">{ticket.nmi}</Descriptions.Item>
              <Descriptions.Item label="Group">{ticket.group}</Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: "12px 0" }} />
            <Button
              type="primary"
              block
              onClick={() => navigate(`/systems/${ticket.systemSn}`)}
            >
              Go to System Detail
            </Button>
          </Card>

          {/* Customer Info */}
          <Card
            bordered={false}
            style={{ borderRadius: 12, marginBottom: 16 }}
            title={
              <Space>
                <UserOutlined />
                <Text strong>Customer</Text>
              </Space>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Name">{ticket.customer.name}</Descriptions.Item>
              <Descriptions.Item label="Email">{ticket.customer.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{ticket.customer.phone}</Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Ticket Metadata */}
          <Card
            bordered={false}
            style={{ borderRadius: 12 }}
            title={<Text strong>Ticket Info</Text>}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Created">{ticket.createdAt}</Descriptions.Item>
              <Descriptions.Item label="Updated">{ticket.updatedAt}</Descriptions.Item>
              <Descriptions.Item label="Assignee">
                {ticket.assignee || <Text type="secondary">Unassigned</Text>}
              </Descriptions.Item>
            </Descriptions>

            {/* Zendesk Integration Placeholder */}
            <Divider style={{ margin: "12px 0" }} />
            <div style={{ padding: 12, background: token.colorFillQuaternary, borderRadius: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                Zendesk Integration
              </Text>
              {ticket.zendeskTicketId ? (
                <Button type="link" href={ticket.zendeskUrl} target="_blank" style={{ padding: 0 }}>
                  View in Zendesk ({ticket.zendeskTicketId})
                </Button>
              ) : (
                <Button size="small">Sync to Zendesk</Button>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </PageLayout>
  );
}
